/**
 * Helper functions para auditoria de mudanças nos dados do candidato
 */

import { FieldValues } from 'react-hook-form';
import { ICandidateData } from '../contexts/types';

/**
 * Extrai as mudanças entre dados antigos e novos do candidato
 * Retorna apenas os campos que foram alterados com valores old/new
 */
export const extractProfileChanges = (
  oldData: ICandidateData,
  newData: FieldValues,
): Record<string, { old: unknown; new: unknown }> => {
  const changes: Record<string, { old: unknown; new: unknown }> = {};

  // Mapa de campos para facilitar a comparação
  const fieldMap: Record<string, { oldValue: unknown; label: string }> = {
    email: {
      oldValue: oldData?.user?.email,
      label: 'E-mail',
    },
    full_name: {
      oldValue: oldData?.user?.full_name,
      label: 'Nome Completo',
    },
    cpf: {
      oldValue: oldData?.cpf,
      label: 'CPF',
    },
    passport_number: {
      oldValue: oldData?.passport_number,
      label: 'Número do Passaporte',
    },
    date_of_birth: {
      oldValue: oldData?.date_of_birth?.us,
      label: 'Data de Nascimento',
    },
    gender: {
      oldValue: oldData?.gender?.id,
      label: 'Sexo',
    },
    marital_status: {
      oldValue: oldData?.marital_status?.id,
      label: 'Estado Civil',
    },
    race: {
      oldValue: oldData?.race,
      label: 'Raça/Cor',
    },
    mother_name: {
      oldValue: oldData?.mother_name,
      label: 'Nome da Mãe',
    },
    nationality: {
      oldValue: oldData?.nationality?.id,
      label: 'Nacionalidade',
    },
    nationality_foreign: {
      oldValue: oldData?.nationality_foreign,
      label: 'Nacionalidade Estrangeira',
    },
    document_type: {
      oldValue: oldData?.document_type?.id,
      label: 'Tipo de Documento',
    },
    document_number: {
      oldValue: oldData?.document_number,
      label: 'Número do Documento',
    },
    document_issuing_body: {
      oldValue: oldData?.document_issuing_body,
      label: 'Órgão Emissor',
    },
    document_issuing_body_uf: {
      oldValue: oldData?.document_issuing_body_uf?.id,
      label: 'UF do Órgão Emissor',
    },
    document_date_of_issuance: {
      oldValue: oldData?.document_date_of_issuance?.us,
      label: 'Data de Emissão do Documento',
    },
    education_level: {
      oldValue: oldData?.education_level?.id,
      label: 'Nível de Escolaridade',
    },
    city_naturality: {
      oldValue: oldData?.city_of_birth?.id,
      label: 'Cidade de Nascimento',
    },
    address_zip_code: {
      oldValue: oldData?.address_zip_code,
      label: 'CEP',
    },
    address_street: {
      oldValue: oldData?.address_street,
      label: 'Rua',
    },
    address_number: {
      oldValue: oldData?.address_number,
      label: 'Número',
    },
    address_complement: {
      oldValue: oldData?.address_complement,
      label: 'Complemento',
    },
    address_neighborhood: {
      oldValue: oldData?.address_neighborhood,
      label: 'Bairro',
    },
    address_state: {
      oldValue: oldData?.address_state?.id,
      label: 'Estado',
    },
    address_city: {
      oldValue: oldData?.address_city?.id,
      label: 'Cidade',
    },
    cellphone: {
      oldValue: oldData?.cellphone,
      label: 'Celular',
    },
    phone: {
      oldValue: oldData?.phone,
      label: 'Telefone',
    },
    nis: {
      oldValue: oldData?.nis,
      label: 'NIS',
    },
  };

  // Itera sobre os campos do formulário
  Object.keys(newData).forEach((fieldKey) => {
    const field = fieldMap[fieldKey];
    if (!field) return;

    const newValue = newData[fieldKey];
    const oldValue = field.oldValue;

    // Remove espaços e caracteres especiais para comparação limpa
    const cleanOld = String(oldValue ?? '').trim();
    const cleanNew = String(newValue ?? '').trim();

    // Se os valores são diferentes, registra a mudança
    if (cleanOld !== cleanNew) {
      changes[field.label] = {
        old: oldValue || 'Não informado',
        new: newValue || 'Não informado',
      };
    }
  });

  return changes;
};

/**
 * Formata as mudanças em uma mensagem legível
 */
export const formatChangesMessage = (changes: Record<string, { old: unknown; new: unknown }>): string => {
  const fieldCount = Object.keys(changes).length;

  if (fieldCount === 0) {
    return 'Nenhuma alteração detectada';
  }

  if (fieldCount === 1) {
    const [field] = Object.keys(changes);
    return `Alterou ${field}`;
  }

  return `Alterou ${fieldCount} campo(s): ${Object.keys(changes).join(', ')}`;
};
